import { useState, useEffect } from 'react';
import { CarListing } from '../../types/dealership';
import { CarListingCard } from './CarListingCard';
import { apiService, CarListing as ApiCarListing } from '../../services/api';
import { toast } from 'sonner';

interface CurrentListingsSectionProps {
  onEditListing: (listing: CarListing) => void;
  onViewDetails: (listing: CarListing) => void;
  dealerId?: number;
  refreshTrigger?: number;
}

export function CurrentListingsSection({ onEditListing, onViewDetails, dealerId = 1, refreshTrigger = 0 }: CurrentListingsSectionProps) {
  const [listings, setListings] = useState<CarListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchListings = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getCarListings({
        dealer_id: dealerId,
        status: 'active',
        limit: 50
      });
      
      // Convert API car listings to frontend format (include downPayment, features for edit form)
      const convertedListings: CarListing[] = response.data.map((apiListing: ApiCarListing) => ({
        id: apiListing.id.toString(),
        model: `${apiListing.year} ${apiListing.make} ${apiListing.model}`,
        type: apiListing.vehicle_type || 'Sedan',
        price: `$${apiListing.price.toLocaleString()}`,
        creditScore: `${apiListing.min_credit_score || 650}+`,
        sellerInfo: 'Verified Dealer',
        financingTerm: `${apiListing.term_months || 60} Months`,
        carName: `${apiListing.year} ${apiListing.make} ${apiListing.model}`,
        carMake: apiListing.make,
        carModel: apiListing.model,
        carBrand: apiListing.vehicle_type || apiListing.make,
        make: apiListing.make,
        mileage: apiListing.mileage?.toString() || '0',
        fuelType: apiListing.fuel_type || 'gasoline',
        transmission: apiListing.transmission || 'automatic',
        exteriorColor: apiListing.color || 'N/A',
        interiorColor: 'N/A',
        carDescription: apiListing.description || '',
        msrp: apiListing.price != null ? `$${apiListing.price.toLocaleString()}` : '',
        downPayment: apiListing.down_payment != null ? apiListing.down_payment.toString() : '',
        minCreditScore: apiListing.min_credit_score?.toString() || '650',
        termDuration: apiListing.term_months?.toString() || '60',
        monthlyPayment: apiListing.monthly_payment?.toString() || '0',
        otherRequirements: Array.isArray(apiListing.features) ? apiListing.features.join('\n') : '',
        carImages: (apiListing.images || []).map((url, idx) => ({
          id: idx + 1,
          imageUrl: url,
          fileName: `image-${idx + 1}`,
          fileSize: 0,
          isPrimary: idx === 0
        })),
        uploadedImageUrls: apiListing.images || [],
        vin: apiListing.vin || '',
        drivetrain: 'FWD',
        year: apiListing.year.toString(),
        dealerInfo: {
          name: apiListing.dealer_name || 'Unknown Dealer',
          type: apiListing.dealer_type || '',
          address: [apiListing.dealer_city, apiListing.dealer_state, apiListing.dealer_country].filter(Boolean).join(', ') ||
            apiListing.location || 'Location not specified',
          hours: '',
          email: apiListing.dealer_email || apiListing.contact_email || '',
          phone: apiListing.dealer_phone || apiListing.contact_phone || ''
        },
        listingNumber: `LISTING${apiListing.id}`
      }));
      
      setListings(convertedListings);
    } catch (error) {
      console.error('Error fetching listings:', error);
      setError('Failed to load car listings');
      toast.error('Failed to load car listings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, [dealerId, refreshTrigger]);

  const handleRefresh = () => {
    fetchListings();
  };

  if (isLoading) {
    return (
      <div className="pt-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-2">Current Listings</h1>
          <p className="text-gray-600 text-center">Loading your car inventory...</p>
        </div>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f91bdc]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-2">Current Listings</h1>
          <p className="text-gray-600 text-center">Manage your car inventory</p>
        </div>
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={handleRefresh}
            className="bg-[#f91bdc] text-white px-6 py-3 rounded-lg hover:bg-[#e015c9] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 text-center sm:text-left">Current Listings</h1>
            <p className="text-gray-600 text-center sm:text-left">Manage your car inventory</p>
          </div>
          <button 
            onClick={handleRefresh}
            className="bg-[#f91bdc] text-white px-4 py-2 rounded-lg hover:bg-[#e015c9] transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {listings.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">No car listings found.</p>
          <p className="text-gray-500">Add your first car listing to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-8">
          {listings.map((listing) => (
            <CarListingCard 
              key={listing.id}
              listing={listing}
              onEditListing={onEditListing}
              onViewDetails={onViewDetails}
            />
          ))}
        </div>
      )}
    </div>
  );
}